import { ValidationExceptionModel } from '../models';
import { transformServerErrorToFormik } from './transform-server-error-to-formik';
import { validateFormikWithJoi } from './validateFormikWithJoi';
import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { FormikConfig, FormikHelpers } from 'formik/dist/types';
import { ObjectSchema, ArraySchema } from 'joi';
import { useEffect, useMemo, useState } from 'react';

interface CustomFormicOptions<T> extends FormikConfig<T> {
  onSubmit: (values: T, formikHelpers: FormikHelpers<T>) => void;
  validationSchema?: ObjectSchema<T> | ArraySchema;
  skipErrorKey?: string;
}

let timeOutStamp = null;

export function useCustomFormik<T>(
  { validationSchema, skipErrorKey, ...options }: CustomFormicOptions<T>,
  serverError?: AxiosError<ValidationExceptionModel>,
) {
  const [errors, setErrors] = useState({});

  const { handleBlur, ...params } = useFormik<T>({
    ...options,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: (formValues, formikHelpers) => {
      timeOutStamp = setTimeout(() => {
        formikHelpers.setSubmitting(false);
      }, 400);

      options.onSubmit(formValues, formikHelpers);
    },
    validate: (formValues: T) => {
      const formikErrors = validationSchema
        ? validateFormikWithJoi<T>(validationSchema, formValues, skipErrorKey)
            .formikErrors
        : {};

      return {
        ...formikErrors,
        ...errors,
      };
    },
  });

  useEffect(
    () => () => {
      clearTimeout(timeOutStamp);
    },
    [],
  );

  const removeError = (name: string): void => {
    if (!name) {
      return;
    }

    delete errors[name];

    setErrors({ ...errors });
  };

  useMemo((): void => {
    const formikErrors = transformServerErrorToFormik(serverError);

    if (!formikErrors) {
      return;
    }

    setErrors(formikErrors);
    params.setErrors(formikErrors);
  }, [serverError]);

  return {
    removeError,
    ...params,
    handleBlur: (event) => {
      const partsOfName = (event?.target?.name || '').split('.');

      removeError(event?.target?.name);

      if (partsOfName.length) {
        removeError(partsOfName[partsOfName.length - 1]);
      }

      if (skipErrorKey) {
        removeError((event?.target?.name || '').replace(skipErrorKey, ''));
        removeError(
          (event?.target?.name || '').replace(`${skipErrorKey}.`, ''),
        );
      }

      handleBlur(event);
    },
  };
}
